import { useState } from 'react';
import type {
  AccionPuerta,
  EditarPuerta,
  EstadoPuerta,
  MaterialPuerta,
  Puerta,
  RespuestaPuerta,
} from '@matias-asset-os/contracts';
import {
  ESTADOS_PUERTA,
  MATERIALES_PUERTA,
  RESPUESTAS_PUERTA,
} from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi, type Lugar } from '../api.js';
import { fechaLarga } from '../componentes.js';
import { Icono } from '../componentes/Iconos.js';
import { SituarPuerta } from './SituarPuerta.js';

/**
 * LA FICHA DE UNA PUERTA.
 *
 * La carga rápida es para la acera; esto es para después: cuando alguien se
 * sienta a trabajar lo que se recogió en la calle. Aquí se corrige lo que se
 * apuntó con prisa, se cambia el estado, se apunta cuándo hay que volver y se
 * miran las fotos de lo que estaba descuidado —el jardín seco, el portón
 * despintado—, que son el argumento de la siguiente conversación.
 *
 * Se guarda ENTERA, como la ficha de una obra: cambiar el estado escribe un
 * apunte en el historial y una llamada por campo obligaría a repetir ese efecto
 * en el navegador.
 */

interface Props {
  idioma: Idioma;
  puerta: Puerta;
  esDireccion: boolean;
  onCerrar: () => void;
  onGuardada: (puerta: Puerta) => void;
  onEliminada: (id: string) => void;
}

export function FichaPuerta({
  idioma,
  puerta,
  esDireccion,
  onCerrar,
  onGuardada,
  onEliminada,
}: Props) {
  const [borrador, setBorrador] = useState<Puerta>(puerta);
  const [guardando, setGuardando] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nuevaAccion, setNuevaAccion] = useState('');
  const [fechaAccion, setFechaAccion] = useState('');
  /*
    SITUAR LA PUERTA EN EL MAPA, DESPUES.

    En la calle el punto sale del GPS, y el GPS de un movil entre edificios se
    equivoca de portal con facilidad: deja la chincheta en la acera de enfrente
    o treinta metros mas alla. Da igual para registrar; no da igual cuando
    alguien vuelve a buscar la casa seis semanas despues.

    Aqui se corrige: se escribe la direccion, se busca, y la chincheta se planta
    donde esta la casa. Es lo mismo que hace la ficha de una obra, con la misma
    puerta del servidor.
  */
  const [localizando, setLocalizando] = useState(false);
  const [lugar, setLugar] = useState<Lugar | null>(null);
  const [opciones, setOpciones] = useState<Lugar[]>([]);
  const [sinLugar, setSinLugar] = useState(false);

  async function localizar(): Promise<void> {
    const consulta = [borrador.direccion, borrador.codigoPostal, borrador.municipio, borrador.zona]
      .map((x) => (x ?? '').trim())
      .filter(Boolean)
      .join(', ');
    if (consulta.length < 3) return;
    setLocalizando(true);
    setSinLugar(false);
    try {
      const { lugares } = await api.mapaBuscar(consulta);
      if (lugares.length === 0) {
        setOpciones([]);
        setSinLugar(true);
        return;
      }
      // Con varias coincidencias se ensenan todas: elegir es del que conoce la
      // calle, no del buscador.
      setOpciones(lugares.length > 1 ? lugares : []);
      aplicarLugar(lugares[0]!);
    } catch {
      setSinLugar(true);
    } finally {
      setLocalizando(false);
    }
  }

  /** Lo encontrado rellena lo que falta; lo escrito a mano no se pisa. */
  function aplicarLugar(encontrado: Lugar): void {
    setLugar(encontrado);
    setOpciones([]);
    cambiar({
      lat: encontrado.lat,
      lon: encontrado.lon,
      ...(encontrado.municipio && borrador.municipio.trim() === ''
        ? { municipio: encontrado.municipio }
        : {}),
      ...(encontrado.zona && borrador.zona.trim() === '' ? { zona: encontrado.zona } : {}),
      ...(encontrado.codigoPostal && borrador.codigoPostal.trim() === ''
        ? { codigoPostal: encontrado.codigoPostal }
        : {}),
    });
  }

  const cambiar = (parte: Partial<Puerta>) => setBorrador((b) => ({ ...b, ...parte }));

  async function guardar(extra?: Partial<Puerta>): Promise<void> {
    setGuardando(true);
    setError(null);
    const final = { ...borrador, ...extra };
    try {
      const datos: EditarPuerta = {
        direccion: final.direccion,
        codigoPostal: final.codigoPostal,
        municipio: final.municipio,
        zona: final.zona,
        lat: final.lat,
        lon: final.lon,
        estado: final.estado,
        respuesta: final.respuesta,
        respuestaTexto: final.respuestaTexto,
        material: final.material,
        contactoNombre: final.contactoNombre,
        contactoTelefono: final.contactoTelefono,
        notas: final.notas,
        fotoPortada: final.fotoPortada,
        acciones: final.acciones,
        historial: final.historial,
        visitadaEn: final.visitadaEn,
      };
      const { puerta: guardada } = await api.guardarPuerta(final.id, datos);
      setBorrador(guardada);
      onGuardada(guardada);
    } catch (e) {
      setError(e instanceof ErrorApi ? t(idioma, e.mensajeClave) : t(idioma, 'error.servidor'));
    } finally {
      setGuardando(false);
    }
  }

  async function subirFotos(ficheros: File[]): Promise<void> {
    setSubiendo(true);
    setError(null);
    try {
      for (const f of ficheros) await api.subirAdjunto('PUERTA', borrador.id, f);
      const { puerta: fresca } = await api.puerta(borrador.id);
      setBorrador(fresca);
      onGuardada(fresca);
    } catch (e) {
      setError(e instanceof ErrorApi ? t(idioma, e.mensajeClave) : t(idioma, 'error.servidor'));
    } finally {
      setSubiendo(false);
    }
  }

  function anadirAccion(): void {
    if (nuevaAccion.trim().length < 2) return;
    const accion: AccionPuerta = {
      id: crypto.randomUUID(),
      texto: nuevaAccion.trim(),
      fecha: fechaAccion || null,
      hecha: false,
      creadaPor: null,
      creadaEn: new Date().toISOString(),
    };
    setNuevaAccion('');
    setFechaAccion('');
    void guardar({ acciones: [accion, ...borrador.acciones] });
  }

  return (
    <section className="tarjeta pap-ficha">
      <div className="tarjeta-cabeza">
        <button type="button" className="enlace" onClick={onCerrar}>
          <Icono nombre="volver" /> {t(idioma, 'app.volver')}
        </button>
        <span className="numerico suave">{borrador.codigo}</span>
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {/* --- Dónde y en qué punto está ------------------------------------- */}
      <div className="campo">
        <label htmlFor="pap-dir">{t(idioma, 'pap.direccion')}</label>
        <input
          id="pap-dir"
          type="text"
          value={borrador.direccion}
          onChange={(e) => cambiar({ direccion: e.target.value })}
        />
      </div>
      <div className="rejilla tres">
        <div className="campo">
          <label htmlFor="pap-zona">{t(idioma, 'pap.zona')}</label>
          <input
            id="pap-zona"
            type="text"
            value={borrador.zona}
            onChange={(e) => cambiar({ zona: e.target.value })}
          />
        </div>
        <div className="campo">
          <label htmlFor="pap-muni">{t(idioma, 'pap.municipio')}</label>
          <input
            id="pap-muni"
            type="text"
            value={borrador.municipio}
            onChange={(e) => cambiar({ municipio: e.target.value })}
          />
        </div>
        <div className="campo">
          <label htmlFor="pap-cp">{t(idioma, 'pap.codigoPostal')}</label>
          <input
            id="pap-cp"
            type="text"
            value={borrador.codigoPostal}
            onChange={(e) => cambiar({ codigoPostal: e.target.value })}
          />
        </div>
      </div>

      {/*
        BUSCAR LA DIRECCION. El punto que trajo el GPS en la calle se afina
        aqui: se escribe la direccion de verdad y la chincheta se planta donde
        esta la casa, no donde estaba el captador.
      */}
      <div className="campo campo-localizar">
        <span className="etiqueta-campo">{t(idioma, 'obras.situarTitulo')}</span>
        <button
          type="button"
          className="secundario"
          disabled={localizando}
          onClick={() => void localizar()}
        >
          <Icono nombre="pin" /> {t(idioma, localizando ? 'obras.situando' : 'obras.situar')}
        </button>
        {lugar && (
          <small className="suave">
            {t(idioma, 'obras.situada')}: {lugar.etiqueta}
          </small>
        )}
        {sinLugar && <small className="suave">{t(idioma, 'obras.noSituada')}</small>}
        {opciones.length > 0 && (
          <div className="lista-marcable">
            {opciones.map((o) => (
              <button
                key={`${o.lat},${o.lon}`}
                type="button"
                className="enlace"
                onClick={() => aplicarLugar(o)}
              >
                {o.etiqueta}
              </button>
            ))}
          </div>
        )}
      </div>

      {/*
        Y SI EL BUSCADOR NO ACIERTA, SE PONE A MANO. Una urbanizacion sin numero
        de calle no la encuentra ningun buscador; arrastrar la chincheta sobre
        el tejado de la casa, si. Es el ultimo recurso y el unico que nunca
        falla.
      */}
      <SituarPuerta
        idioma={idioma}
        lat={borrador.lat}
        lon={borrador.lon}
        onMover={(lat, lon) => cambiar({ lat, lon })}
      />

      <div className="campo">
        <label>{t(idioma, 'pap.estadoTitulo')}</label>
        <div className="pap-rejilla">
          {ESTADOS_PUERTA.map((e) => (
            <button
              key={e}
              type="button"
              className={`pap-opcion${borrador.estado === e ? ' elegida' : ''}`}
              aria-pressed={borrador.estado === e}
              onClick={() => cambiar({ estado: e as EstadoPuerta })}
            >
              {t(idioma, `pap.estado.${e}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="campo">
        <label>{t(idioma, 'pap.queDijeron')}</label>
        <div className="pap-rejilla">
          {RESPUESTAS_PUERTA.map((r) => (
            <button
              key={r}
              type="button"
              className={`pap-opcion${borrador.respuesta === r ? ' elegida' : ''}`}
              aria-pressed={borrador.respuesta === r}
              onClick={() =>
                cambiar({ respuesta: borrador.respuesta === r ? null : (r as RespuestaPuerta) })
              }
            >
              {t(idioma, `pap.respuesta.${r}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="campo">
        <label htmlFor="pap-resp">{t(idioma, 'pap.respuestaOtra')}</label>
        <textarea
          id="pap-resp"
          rows={2}
          value={borrador.respuestaTexto ?? ''}
          onChange={(e) => cambiar({ respuestaTexto: e.target.value || null })}
        />
      </div>

      <div className="campo">
        <label>{t(idioma, 'pap.material')}</label>
        <div className="pap-rejilla">
          {MATERIALES_PUERTA.map((m) => (
            <button
              key={m}
              type="button"
              className={`pap-opcion${borrador.material === m ? ' elegida' : ''}`}
              aria-pressed={borrador.material === m}
              onClick={() => cambiar({ material: m as MaterialPuerta })}
            >
              {t(idioma, `pap.material.${m}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="rejilla dos">
        <div className="campo">
          <label htmlFor="pap-contacto">{t(idioma, 'pap.contacto')}</label>
          <input
            id="pap-contacto"
            type="text"
            value={borrador.contactoNombre ?? ''}
            onChange={(e) => cambiar({ contactoNombre: e.target.value || null })}
          />
        </div>
        <div className="campo">
          <label htmlFor="pap-tel">{t(idioma, 'pap.telefono')}</label>
          <input
            id="pap-tel"
            type="tel"
            value={borrador.contactoTelefono ?? ''}
            onChange={(e) => cambiar({ contactoTelefono: e.target.value || null })}
          />
        </div>
      </div>

      <div className="campo">
        <label htmlFor="pap-notas">{t(idioma, 'pap.notas')}</label>
        <textarea
          id="pap-notas"
          rows={3}
          value={borrador.notas ?? ''}
          onChange={(e) => cambiar({ notas: e.target.value || null })}
        />
      </div>

      <div className="fila-flex" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
        <button type="button" disabled={guardando} onClick={() => void guardar()}>
          {t(idioma, guardando ? 'app.guardando' : 'app.guardar')}
        </button>
        {esDireccion && (
          <button
            type="button"
            className="enlace"
            onClick={() => {
              if (!window.confirm(t(idioma, 'pap.confirmarQuitar'))) return;
              void api
                .eliminarPuerta(borrador.id)
                .then(() => onEliminada(borrador.id))
                .catch(() => setError(t(idioma, 'error.servidor')));
            }}
          >
            {t(idioma, 'checklists.eliminar')}
          </button>
        )}
      </div>

      {/* --- Las fotos: la fachada y lo que estaba descuidado -------------- */}
      <h3>{t(idioma, 'pap.fotos')}</h3>
      <p className="suave">{t(idioma, 'pap.fotosPie')}</p>
      <div className="pap-fotos">
        {borrador.fotos.map((f) => (
          <a key={f.id} href={api.urlAdjunto(f.id)} target="_blank" rel="noreferrer">
            <img src={api.urlAdjunto(f.id)} alt={f.nombreFichero} />
          </a>
        ))}
        <label className="pap-anadir-foto">
          <Icono nombre="camara" /> {t(idioma, subiendo ? 'pap.subiendo' : 'pap.anadirFoto')}
          <input
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              const ficheros = [...(e.target.files ?? [])];
              e.target.value = '';
              if (ficheros.length > 0) void subirFotos(ficheros);
            }}
          />
        </label>
      </div>

      {/* --- Seguimientos y tareas ---------------------------------------- */}
      <h3>{t(idioma, 'pap.seguimientos')}</h3>
      <div className="fila-flex" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
        <input
          type="text"
          className="crecer"
          value={nuevaAccion}
          placeholder={t(idioma, 'pap.nuevoSeguimiento')}
          onChange={(e) => setNuevaAccion(e.target.value)}
        />
        <input type="date" value={fechaAccion} onChange={(e) => setFechaAccion(e.target.value)} />
        <button type="button" disabled={nuevaAccion.trim().length < 2} onClick={anadirAccion}>
          <Icono nombre="mas" /> {t(idioma, 'app.anadir')}
        </button>
      </div>
      {borrador.acciones.length === 0 ? (
        <p className="suave">{t(idioma, 'pap.sinSeguimientos')}</p>
      ) : (
        <ul className="pap-acciones">
          {borrador.acciones.map((a) => (
            <li key={a.id} className={a.hecha ? 'hecha' : ''}>
              <label>
                <input
                  type="checkbox"
                  checked={a.hecha}
                  onChange={() =>
                    void guardar({
                      acciones: borrador.acciones.map((x) =>
                        x.id === a.id ? { ...x, hecha: !x.hecha } : x,
                      ),
                    })
                  }
                />
                <span>{a.texto}</span>
              </label>
              {a.fecha && <small className="suave">{fechaLarga(a.fecha, idioma)}</small>}
            </li>
          ))}
        </ul>
      )}

      {/* --- Lo que ha pasado aquí ---------------------------------------- */}
      <h3>{t(idioma, 'pap.historial')}</h3>
      {borrador.historial.length === 0 ? (
        <p className="suave">{t(idioma, 'pap.sinHistorial')}</p>
      ) : (
        <ul className="pap-historial">
          {borrador.historial.map((h) => (
            <li key={h.id}>
              <b>{h.texto}</b>
              <small className="suave">
                {fechaLarga(h.cuando.slice(0, 10), idioma)}
                {h.quien ? ` · ${h.quien}` : ''}
              </small>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
